import type { FC, ReactNode } from "react";
import { cn } from "./utils/cn";

export interface CollapsibleProps {
  open: boolean;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
}

/**
 * Height-animated reveal with a slight slide. Keeps children mounted so
 * open/close both animate smoothly. Respects prefers-reduced-motion.
 *
 * While closed the content is `visibility: hidden` (in addition to
 * `aria-hidden`) so interactive children are removed from the Tab order.
 * `visibility` participates in the transition, so it only flips to hidden
 * once the closing animation has finished.
 */
const Collapsible: FC<CollapsibleProps> = ({
  open,
  children,
  className,
  contentClassName,
}) => (
  <div
    className={cn(
      "motion-reduce:transition-none grid transition-[grid-template-rows] duration-300 ease-out-soft",
      open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
      className
    )}
    aria-hidden={!open}
  >
    <div className="min-h-0 overflow-hidden">
      <div
        className={cn(
          "motion-reduce:transition-none transition-[transform,opacity,visibility] duration-300 ease-out-soft",
          open
            ? "visible translate-y-0 opacity-100"
            : "pointer-events-none invisible -translate-y-1 opacity-0",
          contentClassName
        )}
      >
        {children}
      </div>
    </div>
  </div>
);

export default Collapsible;
